import type { Reducer } from '@reduxjs/toolkit';
import type { AttachmentData, ChatTemporaryMessageMap } from '../Utils/types';
import type { ResetData, ResetError } from './ApplicationActions';
import { resetReducerErrors, getReducerError, type ReducerError } from '../Utils/reducerError';

import createUuid from '../Utils/createUuid';

export type ChatMessageState = {
  messages: ChatTemporaryMessageMap;
  reducerErrors: ReducerError[];
};

const initialState: ChatMessageState = {
  messages: {},
  reducerErrors: [],
};

export type SetMessageText = { type: 'ChatMessage/SET'; chatId: number; text: string };
export type SetMessageMedia = { type: 'ChatMessage/SET_MEDIA'; chatId: number; attachments: Array<AttachmentData> };
export type SetMessageReply = { type: 'ChatMessage/SET_REPLY'; chatId: number; replyMessageId?: number };
export type SetMentionedUsers = {
  type: 'ChatMessage/SET_MENTIONED_USERS';
  chatId: number;
  mentionedUsers: Array<number>;
};
export type SetMentionedNames = {
  type: 'ChatMessage/SET_MENTIONED_NAMES';
  chatId: number;
  mentionedNames: Array<string>;
};
export type SetFilteredNames = { type: 'ChatMessage/SET_FILTERED_NAMES'; chatId: number; filteredNames: Array<string> };

export type SetChatMediaHeight = { type: 'ChatMessage/SET_CHAT_MEDIA_HEIGHT'; chatId: number; mediaHeight: number };

export type MediaUploadSuccess = { type: 'ChatMessage/MEDIA_UPLOAD_SUCCESS'; chatId: number };

export type SubmitMessage = { type: 'ChatMessage/SUBMIT'; chatId: number; highPriority: boolean };

export type ChatMessageAction =
  | SetMessageText
  | SetMessageMedia
  | SetMessageReply
  | SetMentionedUsers
  | SetMentionedNames
  | SetFilteredNames
  | SetChatMediaHeight
  | MediaUploadSuccess
  | SubmitMessage
  | ResetData
  | ResetError;

export const setMessageText = (chatId: number, text: string): SetMessageText => ({
  type: 'ChatMessage/SET',
  chatId,
  text,
});

export const setMessageMedia = (chatId: number, attachments: Array<AttachmentData>): SetMessageMedia => ({
  type: 'ChatMessage/SET_MEDIA',
  chatId,
  attachments: attachments || [],
});

export const setMessageReply = (chatId: number, replyMessageId?: number): SetMessageReply => ({
  type: 'ChatMessage/SET_REPLY',
  chatId,
  replyMessageId,
});

export const setMentionedUsers = (chatId: number, mentionedUsers: Array<number> = []): SetMentionedUsers => ({
  type: 'ChatMessage/SET_MENTIONED_USERS',
  chatId,
  mentionedUsers: mentionedUsers || [],
});

export const setMentionedNames = (chatId: number, mentionedNames: Array<string> = []): SetMentionedNames => ({
  type: 'ChatMessage/SET_MENTIONED_NAMES',
  chatId,
  mentionedNames: mentionedNames || [],
});

export const setFilteredNames = (chatId: number, filteredNames: Array<string> = []): SetFilteredNames => ({
  type: 'ChatMessage/SET_FILTERED_NAMES',
  chatId,
  filteredNames: filteredNames || [],
});

export const setChatMediaHeight = (chatId: number, mediaHeight: number): SetChatMediaHeight => ({
  type: 'ChatMessage/SET_CHAT_MEDIA_HEIGHT',
  chatId,
  mediaHeight,
});

export const mediaUploadSuccess = (chatId: number): MediaUploadSuccess => ({
  type: 'ChatMessage/MEDIA_UPLOAD_SUCCESS',
  chatId,
});

export const submitMessage = (chatId: number, highPriority: boolean): SubmitMessage => ({
  type: 'ChatMessage/SUBMIT',
  chatId,
  highPriority,
});

const reducer = (state: ChatMessageState = initialState, action: ChatMessageAction): ChatMessageState => {
  // wrap the whole reducer to catch any unexpected exceptions and record them
  // Another component will need to act upon these and potentially send them to Sentry
  try {
    return innerReducer(state, action);
  } catch (err) {
    const reducerErrors: ReducerError[] = state.reducerErrors.slice();
    const uuid = createUuid(0);
    if (err instanceof Error) {
      reducerErrors.push(getReducerError(uuid, 'chatMessage', action, err));
    } else {
      const unknownError = new Error('An unknown error occurred in ChatMessageActions');
      reducerErrors.push(getReducerError(uuid, 'chatMessage', action, unknownError));
    }
    return {
      ...state,

      reducerErrors,
    };
  }
};

const innerReducer = (state: ChatMessageState = initialState, action: ChatMessageAction): ChatMessageState => {
  switch (action.type) {
    case 'Application/RESET_ERROR': {
      const reducerErrors = resetReducerErrors(state.reducerErrors, action);
      return {
        ...state,
        reducerErrors,
      };
    }
    case 'Application/RESET_DATA':
      // Reset the store when logging out
      return {
        ...initialState,
      };

    case 'ChatMessage/SET':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            text: action.text,
          },
        },
      };

    case 'ChatMessage/SET_MEDIA':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            attachments: action.attachments,
          },
        },
      };

    case 'ChatMessage/SET_REPLY':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            replyMessageId: action.replyMessageId,
          },
        },
      };

    case 'ChatMessage/SET_MENTIONED_USERS':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            mentionedUsers: action.mentionedUsers,
          },
        },
      };

    case 'ChatMessage/SET_MENTIONED_NAMES':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            mentionedNames: action.mentionedNames,
          },
        },
      };

    case 'ChatMessage/SET_FILTERED_NAMES':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            filteredNames: action.filteredNames,
          },
        },
      };

    case 'ChatMessage/SET_CHAT_MEDIA_HEIGHT':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            mediaHeight: action.mediaHeight,
          },
        },
      };

    case 'ChatMessage/MEDIA_UPLOAD_SUCCESS':
      return {
        ...state,

        messages: {
          ...state.messages,
          [`${action.chatId}`]: {
            ...(state.messages[`${action.chatId}`] || {}),

            localPath: undefined,
          },
        },
      };

    default:
      return state;
  }
};

export default reducer as Reducer<ChatMessageState>;
